import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  type InstanceProps,
  type InventoryAndContentApi,
  type InventoryAndContentState,
  InventoryContentOption,
  type ParentAccountFeatures,
  TargetableInventoryOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import { type ComparisonMap, Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { usePrevious } from '@openx/utils/hooks/usePrevious.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import isEqual from 'lodash/isEqual';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UnknownAction } from 'redux';
import { TargetingFieldPathProvider, useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { updateMultiParameterCategories } from '../../utils/hooks/updateMultiParameterCategories.ts';
import { CustomOptionsProvider, useCustomOptionsContext } from '../../utils/hooks/useCustomOptionsContext.tsx';
import type { InvalidItemsError } from '../shared/BulkAdd/types.ts';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer.ts';
import { inventoryAndContentDefaultOptions } from '../shared/InventoryAndContent/constants.ts';
import { useAppInventoryState } from '../shared/InventoryAndContent/hooks/useAppInventoryState.ts';
import { getEmptyInventoryState, resetContent, setContent } from '../shared/InventoryAndContent/inventoryHelpers.ts';
import { inventoryApiToState, inventoryStateToApi } from '../shared/InventoryAndContent/utils.ts';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { useInstanceState } from './hooks/useInstanceState.ts';
import { useInventoryAndContent } from './hooks/useInventoryAndContent.ts';
import { InventoryAndContentItems } from './InventoryAndContentItems/InventoryAndContentItems.tsx';
import { deriveComparisonType } from './InventoryAndContentItems/utils.ts';
import { InventoryAndContentReadOnly } from './InventoryAndContentReadOnly.tsx';
import { InventoryAndContentTopBox } from './InventoryAndContentTopBox/InventoryAndContentTopBox.tsx';

export const InventoryAndContent = ({ targetingParams, ...props }: TargetingProps<InventoryAndContentApi>) => {
  const { useInventoriesFetch, useResolveAppGroupIds } = useTargetingContext();

  const { appGroupsMap, isLoading: isResolving } = useResolveAppGroupIds?.(targetingParams?.appgroup?.val) ?? {
    appGroupsMap: new Map<string, string>(),
    isLoading: false,
  };

  const { initState, inventoryPayload, idByInstances } = useMemo(() => {
    const initState = inventoryApiToState(targetingParams, appGroupsMap);
    const inventoryPayload = {
      excludes: { ...targetingParams?.excludes },
      includes: { ...targetingParams?.includes },
    };
    const idByInstances = (targetingParams?._idsByInstances && { ...targetingParams?._idsByInstances }) || null;
    return { idByInstances, initState, inventoryPayload };
  }, [targetingParams, appGroupsMap]);

  const { inventories, loading } = useInventoriesFetch(inventoryPayload, idByInstances);
  const [selectedInventory, setSelectedInventory] = useState<InventoryAndContentState | null>(null);

  const contentInventory = targetingParams &&
    inventories && {
      [InventoryContentOption.INVENTORY]: {
        [TargetableInventoryOption.INCLUDES]: inventories.includes,
        [TargetableInventoryOption.EXCLUDES]: inventories.excludes,
      },
    };

  if (loading || isResolving) return <Loader />;

  return (
    <CustomOptionsProvider value={props.customOptions ?? inventoryAndContentDefaultOptions}>
      <RenderInventoryAndContent
        initState={{ ...initState, ...contentInventory, ...selectedInventory }}
        setSelectedInventory={setSelectedInventory}
        {...props}
      />
    </CustomOptionsProvider>
  );
};

const RenderInventoryAndContent = ({
  onFieldUpdate = () => {
    // no-op
  },
  name,
  readonly,
  field,
  initState,
  setSelectedInventory,
  accountUid,
  customOptions,
  displayContext,
  inventoryVersion,
}: Omit<TargetingProps<InventoryAndContentApi>, 'targetingParams'> & {
  initState: InventoryAndContentState;
  setSelectedInventory: Dispatch<SetStateAction<InventoryAndContentState | null>>;
}) => {
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);
  // default value added for OA, because it doesn't use useAccountFetch hook
  const { useCurrentInstanceState, useAccountFetch = () => ({ account: null, isAccountLoading: false }) } =
    useTargetingContext();

  const { inventoryState, dispatchInventory } = useInventoryAndContent(initState);
  const inventoryStateRef = useRef(inventoryState);
  inventoryStateRef.current = inventoryState;
  const [inputInstanceValue, setInputInstanceValue] = useState<InstanceProps | null>(null);
  const { instanceState, dispatchInstanceState } = useInstanceState('');
  const { useIsAllowed } = usePermissionContext();

  const { isAllowed } = useIsAllowed();
  const isRoot = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE));
  const isInternal = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));
  const hasMarketOpSearch = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.MARKET_OPERATOR_SEARCH));

  const { isBefInstance } = useCurrentInstanceState();
  const isDataUpdated = useMemo(() => !isEqual(initState, inventoryState), [initState, inventoryState]);

  const [appBundleIdDuplicateErrors, setAppBundleIdDuplicateErrors] = useState<InvalidItemsError | null>(null);
  const [inventoryListItems, setInventoryListItems] = useState<InventoryListByIdAPI['values']>();

  const topBoxCustomOptions = useCustomOptionsContext<InventoryContentOption>();
  const {
    setAppNameComparisonType,
    setAppInventoryType,
    setContentOption,
    setBundleIdComparisonType,
    isAppInventoryAllowed,
    contentOption,
    appInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  } = useAppInventoryState<InventoryContentOption>({ topBoxCustomOptions, initState });

  // account data are needed only for Site and Ad Unit targeting
  const shouldFetchAccountData =
    !!customOptions?.availableOptions.includes(InventoryContentOption.SITE) ||
    !!customOptions?.availableOptions.includes(InventoryContentOption.ADUNIT);
  const { account, isAccountLoading } = useAccountFetch(shouldFetchAccountData ? accountUid : '');

  const parentAccountFeatures: ParentAccountFeatures = useMemo(
    () => ({
      isAdUnitTargetingEnabled: !!account?.acl_override?.['advertiser.exchange.adunit_id_targeting.read'],
      isSiteTargetingEnabled: !!account?.acl_override?.['advertiser.exchange.site_id_targeting.read'],
    }),
    [account]
  );

  const previousAccountFeaturesValue = usePrevious(parentAccountFeatures);

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => {
      setSelectedInventory(inventoryState);
      onFieldUpdateExtended(
        field,
        inventoryStateToApi(inventoryState, (isRoot || isInternal || hasMarketOpSearch) && isBefInstance)
      );
    },
    restoreParams: () => {
      setInputInstanceValue(null);
      dispatchInventory({ payload: initState, type: setContent } as unknown as UnknownAction);
      setAppNameComparisonType(
        deriveComparisonType(initState[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined)
      );
      setBundleIdComparisonType(
        deriveComparisonType(initState[InventoryContentOption.APP_BUNDLE_ID]?.val as ComparisonMap | undefined)
      );
    },
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    setSelectedInventory(null);
    dispatchInventory({ type: resetContent } as unknown as UnknownAction);
  }, [dispatchInventory, field, onFieldUpdateExtended, setSelectedInventory]);

  const onRemoveParameter = useCallback(
    (option: InventoryContentOption) => {
      const empty = getEmptyInventoryState();

      const nextState: InventoryAndContentState = {
        ...inventoryState,
        [option]: empty[option],
        ...(option === InventoryContentOption.APP_GROUP && isAppInventoryAllowed
          ? { [InventoryContentOption.APP_BUNDLE_ID]: empty[InventoryContentOption.APP_BUNDLE_ID] }
          : {}),
      };

      setSelectedInventory(nextState);

      dispatchInventory({
        payload: nextState,
        type: setContent,
      } as unknown as UnknownAction);

      updateMultiParameterCategories(field, onFieldUpdateExtended, nextState, state =>
        inventoryStateToApi(state, (isRoot || isInternal || hasMarketOpSearch) && isBefInstance)
      );
    },
    [
      inventoryState,
      field,
      onFieldUpdateExtended,
      setSelectedInventory,
      dispatchInventory,
      isRoot,
      isInternal,
      hasMarketOpSearch,
      isBefInstance,
      isAppInventoryAllowed,
    ]
  );

  // Effect uses inventoryStateRef instead of inventoryState to prevent infinite loop
  useEffect(() => {
    if (parentAccountFeatures !== previousAccountFeaturesValue) {
      const isAdUnitTargetingEnabled = parentAccountFeatures.isAdUnitTargetingEnabled;
      const isSiteTargetingEnabled = parentAccountFeatures.isSiteTargetingEnabled;
      const currentState = inventoryStateRef.current;

      const updatedState = {
        ...currentState,
        [InventoryContentOption.ADUNIT]: isAdUnitTargetingEnabled
          ? currentState[InventoryContentOption.ADUNIT]
          : { op: Intersect.INTERSECTS, val: [] },
        [InventoryContentOption.SITE]: isSiteTargetingEnabled
          ? currentState[InventoryContentOption.SITE]
          : { op: Intersect.INTERSECTS, val: [] },
      };

      setSelectedInventory(updatedState);

      dispatchInventory({
        payload: updatedState,
        type: setContent,
      } as unknown as UnknownAction);
    }
  }, [setSelectedInventory, dispatchInventory, parentAccountFeatures, previousAccountFeaturesValue]);

  const isValidFormat = (type: string): type is InventoryContentOption => {
    return Object.values(InventoryContentOption).includes(type as InventoryContentOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<InventoryContentOption>(
    isValidFormat,
    onDrawerOpen,
    setContentOption
  );

  if (isAccountLoading) return <Loader />;

  return (
    <TargetingFieldPathProvider value={field}>
      <InventoryAndContentReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        readonly={readonly}
        targetingParams={initState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={onRemoveAll}
        onRemoveParameter={onRemoveParameter}
        setInventoryListItems={setInventoryListItems}
        inventoryListItems={inventoryListItems}
        parentAccountFeatures={parentAccountFeatures}
        inventoryVersion={inventoryVersion}
      />
      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <InventoryAndContentTopBox
            targetingParams={inventoryState}
            dispatchInventory={dispatchInventory}
            dispatchInstance={dispatchInstanceState}
            inputInstanceValue={inputInstanceValue}
            setInputInstanceValue={setInputInstanceValue}
            isBefInstance={isBefInstance}
            parentAccountFeatures={parentAccountFeatures}
            accountUid={accountUid}
            setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
            inventoryListItems={inventoryListItems}
            displayContext={displayContext}
            contentOption={contentOption}
            setContentOption={setContentOption}
            // App Inventory specific props
            appInventoryType={appInventoryType}
            setAppInventoryType={setAppInventoryType}
            appNameComparisonType={appNameComparisonType}
            setAppNameComparisonType={setAppNameComparisonType}
            bundleIdComparisonType={bundleIdComparisonType}
            setBundleIdComparisonType={setBundleIdComparisonType}
            inventoryVersion={inventoryVersion}
          />

          <InventoryAndContentItems
            targetingParams={inventoryState}
            readonly={false}
            dispatchInventory={dispatchInventory}
            dispatchInstance={dispatchInstanceState}
            instanceState={instanceState}
            inputInstanceValue={inputInstanceValue}
            isBefInstance={isBefInstance}
            accountUid={accountUid}
            appBundleIdDuplicateErrors={appBundleIdDuplicateErrors}
            setInventoryListItems={setInventoryListItems}
            inventoryListItems={inventoryListItems}
            field={field}
            // App Inventory specific props
            appNameComparisonType={appNameComparisonType}
            setAppNameComparisonType={setAppNameComparisonType}
            bundleIdComparisonType={bundleIdComparisonType}
            setBundleIdComparisonType={setBundleIdComparisonType}
            inventoryVersion={inventoryVersion}
          />
        </TargetingDrawer>
      )}
    </TargetingFieldPathProvider>
  );
};
